import { useSyncExternalStore } from 'react';
import { describeError, isApiError } from '@/api/errors';

export type ToastTone = 'default' | 'success' | 'error';

export interface ToastInput {
  title: string;
  description?: string | undefined;
  tone?: ToastTone;
  /** Shown under an expandable "Details" for support. */
  requestId?: string | null | undefined;
  code?: string | undefined;
  action?: { label: string; onClick: () => void } | undefined;
  duration?: number;
}

export interface ToastItem extends ToastInput {
  id: string;
  open: boolean;
}

let toasts: ToastItem[] = [];
const listeners = new Set<() => void>();
let counter = 0;

function emit(): void {
  for (const listener of listeners) listener();
}

export function toast(input: ToastInput): string {
  counter += 1;
  const id = `t${counter}`;
  toasts = [...toasts.slice(-4), { ...input, id, open: true }];
  emit();
  return id;
}

export function dismissToast(id: string): void {
  toasts = toasts.map((t) => (t.id === id ? { ...t, open: false } : t));
  emit();
  setTimeout(() => {
    toasts = toasts.filter((t) => t.id !== id);
    emit();
  }, 200);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useToasts(): ToastItem[] {
  return useSyncExternalStore(
    subscribe,
    () => toasts,
    () => toasts,
  );
}

export const notify = {
  success(title: string, description?: string): string {
    return toast({ title, description, tone: 'success' });
  },
  info(title: string, description?: string): string {
    return toast({ title, description, tone: 'default' });
  },
  /** Problem details → toast: `detail` as the message, request id under Details. */
  error(error: unknown, title?: string): string {
    const described = describeError(error);
    return toast({
      title: title ?? described.title,
      description: described.detail,
      tone: 'error',
      requestId: described.requestId,
      code: isApiError(error) ? error.code : undefined,
      duration: 8000,
    });
  },
};
